/**
 * client/src/pages/Room.tsx — `/r/:code`, the screen that is a book and four other people.
 *
 * design.md §7. This is the composition point: `sync/room.ts` owns the wire, `sync/useRoomSync.ts`
 * owns the two drift policies, `reader/RoomBar.tsx` and `reader/Presence.tsx` own the chrome, and
 * this file wires them to a `<Reader>` and a `useNarration()` that neither of them knows about.
 *
 * ── §7.6: `/r/:code` joins on open ─────────────────────────────────────────
 * There is no lobby, no confirm step and no sign-in wall. A phone that has never seen the site opens
 * the link and is in the room — the same promise P4 made for `/s/:token`, and for the same reason:
 * the flagship interaction is *somebody sends you a link*, and every step between the tap and the
 * book is a step at which the other person is sitting there waiting. `readAccess` on the book and
 * `canJoinRoom` on the server are what decide; this screen asks nobody anything.
 *
 * ── Where the room drives, and where it must not ────────────────────────────
 * The room owns **the controls**, not the media (movies' idea, and the reason none of this needs a
 * server that streams). So:
 *
 *   · a press of play here becomes `{t:"control"}` and **nothing local happens** — the correction
 *     loop applies the room's answer a moment later, on every device including this one. A local
 *     play that the room then cancels is a quarter-second of audio and a person wondering what they
 *     did wrong.
 *   · in `listen` mode a **page turn** is not a control. LAW 12: browsing never seeks narration.
 *     In `read` mode there is no narration clock; a controller's deliberate forward turn advances
 *     the room's reading anchor, while backward/private browsing clears `following` and moves only
 *     that member's dot.
 *   · `resync()` moves **you**, never them.
 *
 * In `read` mode the sync decision reaches `ReaderNarration.followRequest`, a repeatable navigation
 * request rather than a highlight preference. That distinction is what makes a follower actually
 * load another chapter and lets "Catch up now" navigate again even when the room sid has not moved.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { Block } from "../../../shared/doc.ts";
import type { BookPos } from "../../../shared/position.ts";
import { cueIndexAt, fracInCue } from "../../../shared/narration/cue.ts";
import { Blank } from "../App.tsx";
import { abApi, ApiError } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { buildOrdIndex, type OrdIndex } from "../lib/position.ts";
import { audioEngine } from "../audio/AudioEngine.ts";
import { Reader } from "../reader/Reader.tsx";
import { useNarration, type NarrationBook } from "../reader/useNarration.ts";
import type { ReaderNarration } from "../reader/Reader.tsx";
import {
  BlockedBanner,
  ResumePrompt,
  RoomBar,
  RoomChat,
  RoomFlash,
  TapToJoin,
} from "../reader/RoomBar.tsx";
import {
  MarginDots,
  PresenceLine,
  Reactions,
  domPresenceHost,
  sidOnScreen,
  usePresence,
} from "../reader/Presence.tsx";
import { normalizeCode } from "../sync/room.ts";
import { useRoom, useRoomSync, type RoomTransport } from "../sync/useRoomSync.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * The screen
 * ══════════════════════════════════════════════════════════════════════════ */

export function Room(): ReactElement {
  const { code: rawCode = "" } = useParams<{ code: string }>();
  const code = normalizeCode(rawCode);
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * §7.6: a guest may claim a name; a signed-in user's claim is **ignored** and the account handle
   * substituted, server-side. Sending the handle anyway costs nothing and keeps the two paths one
   * code path — the server is the authority either way, which is the point movies' own comment
   * makes: a member's name used to be whatever the browser claimed, so anyone could sit in a theater
   * as anyone.
   */
  const room = useRoom({ name: user?.displayName || user?.handle || null });

  /* ── join on open ──────────────────────────────────────────────────────── */

  // `joinRoom` and `leave` are `useCallback([])` over a ref, so they are stable for the life of the
  // hook and the code is the only real input. Listing them anyway keeps the dependency array honest
  // — the version of this effect that omitted them and the version that includes them behave
  // identically, and only one of them stays correct if `useRoom` is ever refactored.
  const { joinRoom, leave } = room;
  useEffect(() => {
    if (!code) return;
    joinRoom(code);
    return () => leave();
  }, [code, joinRoom, leave]);

  const snapshot = room.room;
  const bookId = snapshot?.bookId ?? null;

  /* ── the book behind the room ──────────────────────────────────────────── */

  const [book, setBook] = useState<{
    id: string;
    title: string;
    author: string | null;
    coverSha: string | null;
    editionId: string | null;
    sentenceCount: number;
    pageCount: number;
  } | null>(null);
  const [bookError, setBookError] = useState<string | null>(null);

  useEffect(() => {
    if (bookId === null) return;
    let live = true;
    void (async () => {
      try {
        const detail = await abApi.book(bookId);
        if (!live) return;
        setBook({
          id: detail.book.id,
          title: detail.book.title,
          author: detail.book.author ?? null,
          coverSha: detail.book.coverSha ?? null,
          editionId: detail.edition?.id ?? detail.book.currentEditionId ?? null,
          sentenceCount: numberOf(detail, "sentenceCount"),
          pageCount: numberOf(detail, "pageCount"),
        });
        setBookError(null);
      } catch (err) {
        if (!live) return;
        // LAW 17: a book route refuses **404, never 403**, so "not found" here genuinely means "not
        // yours" as often as it means "gone" — and saying which would be the disclosure the 404
        // exists to prevent. The room said the book exists; the book route says it is not ours.
        setBookError(
          err instanceof ApiError && err.notFound
            ? "This room is reading a book you don't have access to."
            : err instanceof Error
              ? err.message
              : "Couldn't open the book.",
        );
      }
    })();
    return () => {
      live = false;
    };
  }, [bookId]);

  const narrationBook = useMemo<NarrationBook | null>(
    () =>
      book === null || book.editionId === null
        ? null
        : {
            bookId: book.id,
            editionId: book.editionId,
            title: book.title,
            author: book.author,
            hasCover: book.coverSha !== null,
            voiceLabel: "Karti",
          },
    [book],
  );

  // `inRoom`: the room owns the speed, so a book's remembered one is not applied over it.
  const base = useNarration(narrationBook, { inRoom: true });

  /* ── ordinals ──────────────────────────────────────────────────────────── */

  /**
   * `BookPos.ord` for the sentence this reader is looking at.
   *
   * ⚠️ **The server's dense ordinal when the payload carries one, and never a magnitude when it does
   * not.** `lib/position.ts` names this hazard by phase number: a client deriving
   * `chapterOrd·10⁷ + blockOrd·10³ + sentOrd` and a server writing `sentences.globalOrd` induce the
   * same *order* and wildly different *distances*, and nothing errors. So `OrdIndex.exact` decides
   * whether "3 pages ahead" is computed at all (see `sentencesPerPage` below), and the ordinal itself
   * is always sent because `comparePos` only needs the order.
   *
   * The chapter payload is fetched by URL rather than lifted out of `<Reader>`: it is the same
   * request the reader just made, so it is an HTTP cache hit, and reaching into the reader for it
   * would mean a prop on a component this session does not own.
   */
  const [ordIndex, setOrdIndex] = useState<OrdIndex | null>(null);
  const [ordChapter, setOrdChapter] = useState<number | null>(null);

  const loadOrdsFor = useCallback(
    async (chapterOrd: number) => {
      if (bookId === null || ordChapter === chapterOrd) return;
      try {
        const res = await fetch(
          `/api/v1/books/${encodeURIComponent(bookId)}/chapters/${chapterOrd}`,
          { credentials: "same-origin", headers: { accept: "application/json" } },
        );
        if (!res.ok) return;
        const body = (await res.json()) as { blocks?: Block[]; startOrd?: number | null };
        if (!Array.isArray(body.blocks)) return;
        setOrdIndex(buildOrdIndex(body.blocks, body.startOrd ?? null));
        setOrdChapter(chapterOrd);
      } catch {
        // No ordinals means no "3 pages ahead" and nothing else — presence still draws dots and
        // ticks, both of which are sid- and ms-based. A failed fetch must not take the room down.
      }
    },
    [bookId, ordChapter],
  );

  const sentencesPerPage =
    ordIndex?.exact && book !== null && book.pageCount > 0 && book.sentenceCount > 0
      ? book.sentenceCount / book.pageCount
      : 0;

  /* ── where I am ────────────────────────────────────────────────────────── */

  const [myPos, setMyPos] = useState<BookPos | null>(null);
  const [visibleEndSid, setVisibleEndSid] = useState<string | null>(null);
  const [following, setFollowing] = useState(true);
  const [layoutNonce, setLayoutNonce] = useState(0);

  /**
   * `onAnchor` fires on every landed relayout and page turn — a handful of times a minute — and is
   * the reader telling us which sentence is at the top of the page in front of it. That is exactly
   * `{t:"at"}`'s trigger, and it is the only place this screen learns where its own reader is.
   */
  /**
   * ⚠️ **`base.onAnchor` is called FIRST, and forgetting that made the room a reader with no voice.**
   *
   * `useNarration.onAnchor` is not a notification — it is the hook's *only* input. It sets
   * `anchorRef` and `anchorPageId`, and everything downstream hangs off that: which page the plan is
   * polled for, which page `state` describes, which page `onNarrate` would generate. The plug this
   * screen builds spreads `...base` and then overrides `onAnchor`, so the room's version **replaced**
   * it rather than adding to it, and `useNarration` never learned which page the reader was on.
   *
   * Measured, before the fix: the same reader on the same sentence reported `data-narration-state`
   * `"ready"` on `/b/:id/read` and `"missing"` on `/r/:code`, and in the room it made **zero**
   * `GET /pages/:id/narration` requests — over a page with 99.5 s of finished audio on disk. The bar
   * said *"This page hasn't been narrated yet"* and offered to spend GPU regenerating it. Nothing
   * errored, no request failed, and every unit test passed: the two `onAnchor`s have the same type.
   */
  const baseAnchor = base?.onAnchor ?? null;
  const onAnchor = useCallback(
    (sid: string | null) => {
      baseAnchor?.(sid);
      setLayoutNonce((n) => n + 1);
      if (sid === null) return;
      const ord = ordIndex?.ordOf(sid);
      setMyPos((prev) => {
        if (prev !== null && prev.sid === sid) return prev;
        return {
          sid,
          ord: ord ?? 0,
          // LAW 3: `ms` is advisory. A reading anchor has no honest millisecond behind it, and 0 is
          // the identity on that axis rather than a claim about it. Members render each other's
          // ticks from this and nothing ever seeks to it.
          ms: 0,
          // LAW 9: absent ≠ zero — but `frac` is not absent here. The anchor IS the head of the
          // sentence, so 0 is the measurement, not a default.
          frac: 0,
        };
      });
      const chapterOrd = Number.parseInt(sid.slice(1, 4), 10);
      if (Number.isFinite(chapterOrd)) void loadOrdsFor(chapterOrd);
    },
    [baseAnchor, ordIndex, loadOrdsFor],
  );

  /* ── presence host ─────────────────────────────────────────────────────── */

  /**
   * The reader's own elements, found in the document.
   *
   * `.pf-track` and `.pf-frame` are `reader-page.css`'s structure and `Reader.tsx` renders exactly
   * one of each. A `querySelector` is used rather than a ref because a ref would be a prop on
   * `<Reader>`; it is re-resolved on `layoutNonce`, which is bumped by the same `onAnchor` that every
   * turn and every relayout already fires.
   */
  const [els, setEls] = useState<{ track: HTMLElement | null; frame: HTMLElement | null }>({
    track: null,
    frame: null,
  });
  useEffect(() => {
    setEls({
      track: document.querySelector<HTMLElement>(".pf-track"),
      frame: document.querySelector<HTMLElement>(".pf-frame"),
    });
  }, [layoutNonce, book?.id]);

  const presenceHost = useMemo(() => domPresenceHost(els.track, els.frame), [els]);

  const renderings = usePresence(
    {
      members: snapshot?.members ?? [],
      myKey: room.myKey,
      myPos,
      sentencesPerPage,
      chapterOffsetMs: base?.chapter?.pageOffsetMs ?? 0,
      chapterDurationMs: base?.chapter?.durationMs ?? 0,
    },
    presenceHost,
    layoutNonce,
  );

  /* ── the sync loop ─────────────────────────────────────────────────────── */

  /**
   * ⚠️ **`AudioEngine` satisfies `RoomTransport` structurally — this is an annotation, not a cast.**
   *
   * `test/room-client.test.ts` carries the type-level assertion that keeps it that way, the same way
   * `test/cue-sync.test.ts` pins `NarrationClock`: the cost of a structural seam is that a rename on
   * either side is silent, so the assertion is what fails `npm run check` the moment the two stop
   * agreeing rather than the room stopping working at a page boundary with nothing in the console.
   */
  const transport: RoomTransport | null = audioEngine;

  /**
   * The room's anchor is on a page this client does not hold. `useNarration.onPlayFrom` is the one
   * call that knows how to get there — it re-checks `ensure`, resolves the artifact and opens it —
   * and it is deliberately the only page-changing thing the sync loop can reach.
   *
   * Fire-and-forget by design: it returns long before the artifact lands, which is exactly why the
   * loop's load latch is a **deadline** and not this promise. See `loadUntil` in `useRoomSync.ts`.
   */
  const onPlayFromBase = base?.onPlayFrom ?? null;
  const onLoadPage = useCallback(
    (at: BookPos) => {
      onPlayFromBase?.(at.sid);
    },
    [onPlayFromBase],
  );

  const onMyPage = useCallback((sid: string) => sidOnScreen(presenceHost, sid), [presenceHost]);

  /**
   * **Where the voice actually is**, resolved on this client's own cue list (LAW 3).
   *
   * ⚠️ **Not `myPos`.** `myPos` comes from `onAnchor`, which is *the sentence at the top of the page
   * in front of this reader* — a browsing position, and LAW 12 says browsing is private. Sending it
   * as a control's `at` recorded the wrong sentence on every press: P5's gate has the guest pausing
   * while `c005.b0001.s0` was lit and reporting `at: "c005.b0000.s0"`, and read it as a pass.
   *
   * `ord` is this client's best guess and the server re-derives it from the `sid` (`ws.ts`'s
   * `normalizePos`), which is what makes it safe to send one at all — `lib/position.ts`'s index is
   * per-chapter and falls back to a magnitude on a scale nothing else uses.
   */
  const spokenPos = useCallback((): BookPos | null => {
    const audio = transport?.currentPage?.audio;
    const cues = audio?.cues;
    if (transport === null || !cues || cues.length === 0) return null;
    const ms = transport.masterMs();
    const cue = cues[cueIndexAt(cues, ms)];
    if (cue === undefined) return null;
    return { sid: cue.sid, ord: ordIndex?.ordOf(cue.sid) ?? myPos?.ord ?? 0, ms, frac: fracInCue(cue, ms) };
  }, [transport, ordIndex, myPos]);

  const [snapTarget, setSnapTarget] = useState<{ sid: string; seq: number } | null>(null);
  const requestSnap = useCallback((sid: string) => {
    setSnapTarget((prev) => ({ sid, seq: (prev?.seq ?? 0) + 1 }));
  }, []);

  const sync = useRoomSync({
    room: snapshot,
    playback: room.playback,
    serverNow: room.serverNow,
    myKey: room.myKey,
    canControl: room.canControl,
    control: room.control,
    sendAt: room.sendAt,
    reportNotReady: room.reportNotReady,
    rejoinNonce: room.rejoinNonce,
    transport: room.playback?.mode === "listen" ? transport : null,
    // The load window is owned by `useRoomSync` (`loadUntil`), which is the only place that knows
    // when it issued one. This flag is for a *caller-side* load — a chapter the reader itself is
    // fetching — and P5 has none, so it is honestly false rather than a second copy of the latch.
    loading: false,
    // §7.4's "never fight a user dragging the scrubber". The drag lives in `Scrubber.tsx` inside
    // `Reader.tsx`, and reaching it needs a prop on `ReaderProps` this session does not own — so
    // this is the one input that is not yet real. The exposure is bounded: a drag commits on
    // release (Scrubber.tsx's own design), so the worst case is one correction landing during a
    // drag rather than a fight for its duration. Listed in the handover.
    scrubbing: false,
    onLoadPage,
    onMyPage,
    onSnap: requestSnap,
    following,
    myPos,
    visibleEndSid,
    /**
     * §7.5. What this member is waiting for, which is what `{t:"notready"}` carries.
     *
     * ⚠️ **`pageId` is the load-bearing field and it used to be `null`.** `ws.ts`'s `notready`
     * handler opens with `if (!pageId) return;` — it has to `planPage()` the page before it may
     * believe a block, so a report with no page is dropped without a reply. With `pageId: null` here
     * the `generating` block was unreachable from a browser: the room paused for nobody, no banner
     * ever rendered, and the server never queued the page the room was actually waiting on. Every
     * test of the path passed, because they all send the message by hand.
     */
    pending:
      base === null
        ? null
        : {
            state: base.state ?? "missing",
            // LAW 9 on the way *out* as well as in: an absent estimate stays absent. `?? 0` here
            // would put "about 0 seconds" on four screens.
            etaSec: base.etaSec ?? null,
            pageId: base.waitingPageId ?? null,
            // Two facts about THIS device, and the reason §7.5's blocks are reachable at all. See
            // `useRoomSync`'s block effect: keying `generating` on the planner's string missed every
            // case where the voice service refused, and `buffering` had no sender in any browser.
            waiting: base.waiting ?? false,
            stalled: base.stalled ?? false,
          },
  });

  /* ── the plug the reader gets ──────────────────────────────────────────── */

  /**
   * The base narration plug with every **intent** rerouted through the room.
   *
   * `page`, `clock`, `playing`, `frames`, `state` and `chapter` pass through untouched — those are
   * observations of the local engine and the room does not own them. `onPlayPause`, `onSeek`,
   * `onRate` and `onPlayFrom` are replaced, because they are the four things that must move
   * everybody or nobody.
   *
   * ⚠️ `onNarrate` and `onRetry` pass through **unrouted and that is correct**: spending GPU is a
   * per-actor, per-quota act (§4.8, §6.5) and not a room control. §7.5's room-scoped ensure — keep 6
   * pages ahead at priority 20 — is the server's job precisely so that a room does not have to
   * arrange it by having every member press Narrate at once.
   */
  // Destructured, because `sync` is a fresh object every render while the three callbacks on it are
  // `useCallback`-stable. Depending on `sync` itself would rebuild the plug on every render — and
  // `Reader.tsx` says of this object, in as many words, "Identity matters: pass a stable object. A
  // new object each render re-arms every guard."
  const { intendPlayPause, intendSeek, intendRate, intendAdvance } = sync;

  /**
   * ⚠️ **The page boundary, on the wire. This is what makes a room able to play a chapter through.**
   *
   * The engine is the only thing that knows it crossed one — `advance()` is a one-shot armed once per
   * page and reached from `ended`, from `timeupdate` and from the rAF check, and it emits exactly one
   * `{type:"page", via:"advance"}` when it fires. Every other `via` is somebody *else* deciding:
   * `open` and `resume` are this client being moved (often by the room's own `{do:"load"}`, which
   * would make this an echo), and `artifact` is a re-narration swapped onto the same page, which is
   * not a turn at all. Inferring the boundary from a changed `pageId` instead would confuse all four.
   *
   * `useRoomSync.intendAdvance` decides whether to send: an engine boundary is refused unless this
   * member holds the remote and the room is actually playing in `listen` mode. Read-mode manual
   * turns enter through `onReadAdvance` below and have no playback clock to gate on.
   *
   * The `at` is the **first cue of the new page**, not the old one's last: `advance` names where the
   * room now is, and the server re-derives its `ord` and `ms` off the stored axis.
   */
  useEffect(() => {
    // Null on a host with no `<audio>` — the same guard `transport` carries. There is no room
    // without a transport, so there is no boundary to report either.
    if (audioEngine === null) return;
    return audioEngine.subscribe((ev) => {
      if (ev.type !== "page" || ev.via !== "advance") return;
      const cue = ev.page.audio.cues[0];
      if (cue === undefined) return;
      intendAdvance({ sid: cue.sid, ord: ordIndex?.ordOf(cue.sid) ?? 0, ms: cue.start, frac: 0 });
    });
  }, [intendAdvance, ordIndex]);

  const onReadAdvance = useCallback(
    (sid: string) => {
      if (room.playback?.mode !== "read") return;
      intendAdvance({ sid, ord: ordIndex?.ordOf(sid) ?? 0, ms: 0, frac: 0 });
    },
    [room.playback?.mode, intendAdvance, ordIndex],
  );

  const plug = useMemo<ReaderNarration | null>(() => {
    if (base === null) return null;
    return {
      ...base,
      // §11 and `Reader.tsx`: `spokenSid` is "P1's seam, and P5's co-read follower". In `listen` mode
      // it is ignored (the page drives `useCueSync`); in `read` mode it is what pins the read-along
      // and what `repaginate()` lands on.
      spokenSid: room.playback?.mode === "read" ? (snapTarget?.sid ?? null) : base.spokenSid,
      followRequest: room.playback?.mode === "read" ? snapTarget : null,
      following,
      onVisibleEnd: setVisibleEndSid,
      onReadAdvance: room.playback?.mode === "read" ? onReadAdvance : null,
      onAnchor,
      /**
       * ⚠️ **Which sentence a press of the button names depends on which way it goes, and getting
       * that backwards breaks one of the two directions every time.**
       *
       * **Pausing** — the room is playing. The honest `at` is where the *voice* is, on this client's
       * own cue list. `myPos` is the reading anchor, the sentence at the top of the page, and P5's
       * gate has the guest pausing while `c005.b0001.s0` was lit and recording `c005.b0000.s0`,
       * scored as a pass. §7.6's resume then offers the wrong sentence on Wednesday.
       *
       * **Playing** — the room is paused. The honest `at` is where the *reader* is. This is §5.6's
       * "Play from here" and LAW 12's only escape hatch: turning a page never moves the narration, so
       * pressing play is how somebody who has walked ahead says *read from what I am looking at*.
       * Naming the spoken sentence here was measured, in the gate, as clause 18 failing: A browsed to
       * chapter V, pressed play, and the room stayed on chapter I — because the engine still held
       * chapter I's artifact, so `spokenPos()` truthfully reported a sentence nobody was looking at.
       *
       * `?? myPos` on the pause side covers the window before any cue list exists.
       */
      onPlayPause: () => intendPlayPause(room.playback?.playing === true ? (spokenPos() ?? myPos) : myPos),
      onSeek: (to) =>
        intendSeek({
          sid: to.sid,
          ord: ordIndex?.ordOf(to.sid) ?? myPos?.ord ?? 0,
          ms: 0,
          frac: to.frac,
        }),
      onRate: (rate) => intendRate(rate),
      /**
       * LAW 12, both halves. Turning a page never moves the narration — and it *does* mean this
       * member has browsed away, which is precisely what stops them counting as `behind`. Until this
       * line the room heard `following: true` from a reader that had cleared its own flag two pages
       * ago, and `read` mode's deadline-free `behind` gate held the room there.
       */
      onFollowingChange: (on: boolean) => {
        setFollowing(on);
        room.setFollowing(on);
      },
      onPlayFrom: (sid) => intendSeek({ sid, ord: ordIndex?.ordOf(sid) ?? 0, ms: 0, frac: 0 }),
      // Skips, the sleep timer and the Listen view drive the local engine directly — one member,
      // not the room. Off until they are routed through `sync` like the four intents above.
      listen: null,
      inRoom: true,
    };
  }, [
    base,
    room.playback?.mode,
    snapTarget,
    following,
    onReadAdvance,
    onAnchor,
    intendPlayPause,
    intendSeek,
    intendRate,
    myPos,
    ordIndex,
    spokenPos,
    room,
  ]);

  /* ── the room chrome takes its own space out of the book ───────────────── */

  /**
   * ⚠️ **This is what stops the room bar sitting on top of the last lines of every page.**
   *
   * The stack below is `position: fixed; bottom: 0` and lives *outside* `<Reader>`, so nothing in the
   * reader's flex column knows it is there. P2's transport is `flex: 0 0 auto` **inside** that
   * column and therefore shrinks the stage; P5's chrome shrank nothing, and covered roughly 100–140
   * px of a 390×844 phone — the bottom three to five lines of every column, the progress footer, and
   * the playback bar. Worse than cosmetic: a narrated sentence could be lit underneath it while
   * `sidOnScreen` still reported it on screen, so the margin dots and `onMyPage` agreed with the bug.
   *
   * A ResizeObserver rather than a one-off measurement, because the stack's height changes with the
   * room: the blocked banner appears, the resume prompt appears once and never again, chat opens.
   * Written to `documentElement` so `.ab-reader`'s `padding-bottom` can read it — the reader is the
   * element whose stage `--frame-h` is measured from, so this is the value pagination has to respect.
   * Cleared on unmount: `/b/:id/read` must not inherit a room's chrome height.
   */
  const chromeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = chromeRef.current;
    const root = document.documentElement;
    if (el === null) return;
    const write = (): void => root.style.setProperty("--ab-room-chrome-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    write();
    const ro = new ResizeObserver(write);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--ab-room-chrome-h");
    };
    // `snapshot === null` short-circuits the render below, so the element does not exist until there
    // is a room; re-arming on the room's identity is what attaches the observer once it does.
  }, [snapshot === null, book === null]);

  /* ── chat unread ───────────────────────────────────────────────────────── */

  const [chatOpen, setChatOpen] = useState(false);
  const seenChat = useRef(0);
  useEffect(() => {
    if (chatOpen) seenChat.current = room.chat.length;
  }, [chatOpen, room.chat.length]);
  const unread = chatOpen ? 0 : Math.max(0, room.chat.length - seenChat.current);

  /* ── render ────────────────────────────────────────────────────────────── */

  if (room.error !== null) return <RoomGone error={room.error} text={room.errorText} code={code} />;
  if (bookError !== null) return <RoomGone error="other" text={bookError} code={code} />;
  if (snapshot === null || book === null) return <Blank />;

  return (
    <div className="ab-reader-route" style={{ position: "relative" }}>
      <Reader
        bookId={book.id}
        narration={plug}
        reactionRoomCode={code}
        // The durable write happens first in the sentence menu. Only after it succeeds do we reuse
        // the room's existing three-second float, so a failed save never looks successful to the
        // other people reading with us.
        onRoomReaction={room.sendReaction}
      />

      {/* Presence sits over the frame. `pointer-events: none` on the container so nothing here can
          eat a tap meant for the page-turn zones underneath — a margin is exactly where a thumb
          lands, and a dot that swallowed turns would be the most confusing bug in the product. */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <MarginDots renderings={renderings} />
        <Reactions reactions={room.reactions} host={presenceHost} layoutNonce={layoutNonce} />
      </div>

      {sync.needsGesture ? <TapToJoin roomName={snapshot.name} onTap={sync.tapToJoin} /> : null}

      <div
        ref={chromeRef}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          display: "grid",
          gap: 0,
        }}
      >
        {room.resumeOffer !== null ? (
          <ResumePrompt
            roomAt={room.resumeOffer.at}
            since={room.resumeOffer.since}
            roomLabel={chapterLabel(room.resumeOffer.at.sid)}
            /**
             * §7.6: the room bookmark and your personal bookmark are **different rows** and the app
             * asks once.
             *
             * `personal` is the server's own `positions` row for this member, and it is null when
             * there is none *or* when it belongs to a superseded edition — LAW 2, a position from an
             * edition that is no longer on screen is omitted rather than reinterpreted. Falling back
             * to `myPos` here would quietly re-introduce exactly that: `myPos` is where this reader
             * happens to be *right now*, which is not a bookmark and would make the second button a
             * no-op wearing a bookmark's label.
             */
            mineLabel={
              room.resumeOffer.personal ? chapterLabel(room.resumeOffer.personal.sid) : null
            }
            onResumeTogether={() => {
              room.dismissResume();
              sync.resync();
            }}
            // "Where you left off" moves THIS reader and nobody else, which is why it snaps rather
            // than emitting a control. Nothing auto-plays on either branch — §7.6: "it cannot, and
            // it shouldn't."
            onResumeMine={() => {
              const personal = room.resumeOffer?.personal ?? null;
              room.dismissResume();
              if (personal) requestSnap(personal.sid);
            }}
            onDismiss={() => room.dismissResume()}
          />
        ) : null}

        {/* §7.5 / LAW 20. A block that hit its deadline dropped, the room moved on, and this is the
            sentence that says so. A room that simply started again with no explanation is the most
            alarming version of the feature. */}
        {room.flash ? (
          <RoomFlash flash={room.flash} onDismiss={room.dismissFlash} />
        ) : null}

        {room.playback?.blockedBy?.length ? (
          <BlockedBanner
            blockedBy={room.playback.blockedBy}
            members={snapshot.members}
            myKey={room.myKey}
            onResync={() => {
              setFollowing(true);
              room.setFollowing(true);
              sync.resync();
            }}
          />
        ) : null}

        {chatOpen ? (
          <RoomChat
            messages={room.chat}
            members={snapshot.members}
            myKey={room.myKey}
            onSend={room.sendChat}
            onClose={() => setChatOpen(false)}
          />
        ) : null}

        <div style={{ padding: "0 var(--space-3) var(--space-2)", background: "var(--sheet)" }}>
          <PresenceLine
            renderings={renderings}
            // Local only. It turns THIS reader's page and emits no intent — §7.1's `resync()` shape,
            // which is "put me where they are" and never "put them where I am".
            onGoTo={requestSnap}
          />
        </div>

        <RoomBar
          room={snapshot}
          playback={room.playback}
          myKey={room.myKey}
          canControl={room.canControl}
          connected={room.connected}
          following={following}
          onFollow={(on) => {
            setFollowing(on);
            room.setFollowing(on);
            if (on && room.playback?.at.sid) requestSnap(room.playback.at.sid);
          }}
          onResync={() => {
            setFollowing(true);
            room.setFollowing(true);
            sync.resync();
          }}
          onLeave={() => {
            room.leave();
            navigate(`/b/${encodeURIComponent(book.id)}`);
          }}
          /**
           * §7.6's close, and §6.5's way down. Nothing local is cleared: the server answers with
           * `{t:"ended", reason:"host-ended"}`, which becomes `<RoomGone>`'s "The room ended" screen
           * — the sentence that has always been in that file describing a button that did not exist.
           */
          onEnd={() => room.endRoom()}
          onOpenChat={() => setChatOpen((v) => !v)}
          unreadChat={unread}
          onReact={(emoji) => room.sendReaction(emoji, myPos?.sid)}
          origin={location.origin}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The ways in that do not work
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Every terminal state of a room, in one screen, each saying which one it is.
 *
 * "The room is gone" and "you were removed from the room" and "this book was taken down" send a
 * person to three different next actions, and a single "something went wrong" sends them to reload
 * the page — which for `kicked` puts them straight back into the refusal.
 */
function RoomGone({
  error,
  text,
  code,
}: {
  error: NonNullable<ReturnType<typeof useRoom>["error"]>;
  text: string | null;
  code: string;
}): ReactElement {
  const copy: Record<string, { title: string; detail: string }> = {
    notfound: {
      title: "No room with that code.",
      detail: `Nothing is open at ${code}. Codes are case-insensitive, so it is the characters that are wrong, not the capitals.`,
    },
    locked: { title: "This room needs a password.", detail: "Ask whoever sent you the link for it." },
    throttled: {
      title: "Too many tries.",
      detail: "Six wrong passwords in a minute. Wait a moment and try again.",
    },
    kicked: { title: "You were removed from this room.", detail: "The host ended your seat." },
    ended: { title: "The room ended.", detail: "Whoever started it closed it." },
    "access-revoked": {
      title: "You lost access to this book.",
      detail: "The owner changed who can see it, and the room went with it.",
    },
    "taken-down": { title: "This book was taken down.", detail: "It is not readable while that stands." },
  };
  const c = copy[error] ?? { title: "Couldn't open that room.", detail: text ?? "" };

  return (
    <div className="ab-app">
      <main className="ab-main">
        <div className="ab-empty">
          <h2>{c.title}</h2>
          <p>{c.detail}</p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Back to the shelf
          </Link>
        </div>
      </main>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Small things
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * "Chapter 7" from a sid.
 *
 * ⚠️ A **chapter**, and never a page. §5.1: *"Page numbers are never persisted"* — a page number is
 * this device's page number at this device's font size, and a resume prompt that offered "page 112"
 * would offer a different place on the phone than on the laptop, for the same room, on the same
 * night.
 */
function chapterLabel(sid: string): string {
  const n = Number.parseInt(sid.slice(1, 4), 10);
  return Number.isFinite(n) ? `Chapter ${n}` : "the start";
}

function numberOf(body: unknown, key: string): number {
  const walk = (o: unknown): number | null => {
    if (!o || typeof o !== "object") return null;
    const v = (o as Record<string, unknown>)[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    return null;
  };
  const b = body as Record<string, unknown> | null;
  return walk(b) ?? walk(b?.["book"]) ?? walk(b?.["edition"]) ?? 0;
}

export default Room;
